/**
 * @fileoverview Viewer-vs-tenant access classification.
 * @description `/identity/me`'s privileges answer "can this role open this page at all" — a flat,
 * role-level gate identical for every user holding the same role. They carry no hierarchy or
 * ownership information, so they cannot answer the separate question TenantDetailModal needs
 * answered for a *specific* record: is the current owner, a supervisor whose scope includes it, a
 * previous owner (read-only), or someone outside the record entirely. This file is the one place
 * that computes that, from the already-built hierarchy scope resolver.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { isPendingApproval } from '@/features/sales/lib/prospect-approval.utils'
import { resolveScopedUserIds } from '@/features/sales/lib/sales-hierarchy.utils'

import type { SalesUserRecord, Target, Tenant } from '@/features/sales/types/tenant.types'

export interface TenantAccess {
  /** The viewer is this tenant's current target owner. */
  isOwner: boolean
  /** The viewer is Super Admin, or the tenant's current owner is in the viewer's resolved
   * downline — true whenever `canView` is true and `isOwner`/`isPreviousOwner` don't already cover
   * it. */
  isInScope: boolean
  /** The viewer previously owned this tenant (per `ownerHistory`) but isn't the current owner —
   * "Previous owners retain read-only access to transferred prospects." */
  isPreviousOwner: boolean
  /** Whether the viewer may see this record at all (owner, in scope, or a previous owner). False
   * means the modal should show an access-denied message instead of the tenant's content. */
  canView: boolean
  /** Only Super Admin may confirm payment. */
  canConfirmPayment: boolean
}

// Who works the unassigned backlog: an approved prospect with no owner yet belongs to no one's
// downline, so Sales Heads and Managers (who assign it) are in scope for it. Super Admin is in scope
// for everything anyway.
const UNASSIGNED_PROSPECT_ROLES = ['Sales Head', 'Sales Manager']

/**
 * @description Whether a viewer may see a prospect that's still pending approval: its maker, its
 * approver (their Sales Head) and Super Admin — read-only, since approving is the only thing to do
 * with it (see canApproveProspect).
 * @param {Tenant} tenant - The pending prospect.
 * @param {string} viewerUserId - The viewer's roster id.
 * @param {string} viewerRole - The viewer's active role name.
 * @returns {boolean} Whether the viewer may see it.
 */
function canViewPendingProspect(tenant: Tenant, viewerUserId: string, viewerRole: string): boolean {
  if (viewerRole === 'Super Admin') return true
  return (
    Boolean(viewerUserId) &&
    (tenant.approverId === viewerUserId || tenant.createdById === viewerUserId)
  )
}

/**
 * @description Classifies a viewer's relationship to a tenant record.
 * @param {Tenant} tenant - The tenant being viewed.
 * @param {string} viewerUserId - The signed-in user's sales-roster id (empty/unmatched for a role,
 * such as Super Admin, with no roster entry).
 * @param {string} viewerRole - The signed-in user's active role name (e.g. 'Super Admin', 'Sales
 * Head').
 * @param {SalesUserRecord[]} roster - The full sales-user roster, for hierarchy resolution.
 * @returns {TenantAccess} The viewer's access to this specific tenant. An unassigned prospect is in
 * scope for Sales Heads and Managers, who assign it; one pending approval is view-only, for its
 * maker, its approver and Super Admin.
 */
export function classifyTenantAccess(
  tenant: Tenant,
  viewerUserId: string,
  viewerRole: string,
  roster: SalesUserRecord[],
): TenantAccess {
  if (isPendingApproval(tenant)) {
    return {
      isOwner: false,
      isInScope: false,
      isPreviousOwner: false,
      canView: canViewPendingProspect(tenant, viewerUserId, viewerRole),
      canConfirmPayment: false,
    }
  }

  const isOwner = Boolean(tenant.currentOwnerId) && tenant.currentOwnerId === viewerUserId
  const isPreviousOwner =
    !isOwner && tenant.ownerHistory.some((transfer) => transfer.toOwnerId === viewerUserId)

  const scopedIds = resolveScopedUserIds(viewerUserId, viewerRole, roster)
  const isInScope =
    scopedIds === null ||
    (tenant.currentOwnerId === null
      ? UNASSIGNED_PROSPECT_ROLES.includes(viewerRole)
      : scopedIds.includes(tenant.currentOwnerId))

  return {
    isOwner,
    isInScope,
    isPreviousOwner,
    canView: isOwner || isInScope || isPreviousOwner,
    canConfirmPayment: viewerRole === 'Super Admin',
  }
}

/** The record-level actions a viewer may take on one tenant, each already gated by the viewer's
 * access and the tenant's/target's current state. */
export interface TenantActionPermissions {
  canEdit: boolean
  canArchive: boolean
  canRestore: boolean
  canEditTarget: boolean
  canUpdateStage: boolean
  canScheduleFollowUp: boolean
  canConfirmPayment: boolean
  canReassign: boolean
  canAddActivity: boolean
}

/**
 * @description Resolves which actions a viewer may take on a tenant — the single rule set behind
 * both TenantDetailModal's inline actions and a table row's actions menu, so the two can never
 * disagree.
 * @param {Tenant} tenant - The tenant the actions apply to.
 * @param {Pick<Target, 'status'> | undefined} target - The tenant's relevant target (see
 * pickRelevantTarget), if it has one.
 * @param {TenantAccess} access - The viewer's access, from classifyTenantAccess.
 * @returns {TenantActionPermissions} Which actions the viewer may take.
 */
export function resolveTenantActions(
  tenant: Tenant,
  target: Pick<Target, 'status'> | undefined,
  access: TenantAccess,
): TenantActionPermissions {
  const isOpenProspect = (access.isOwner || access.isInScope) && tenant.status === 'Prospect'

  return {
    // "Edit Prospect": only before conversion, for the owner or a supervisor in scope — no role
    // exception once Active Client, not even Super Admin (see updateTenant's own doc comment).
    canEdit: isOpenProspect,
    canArchive: isOpenProspect && !tenant.archived,
    canRestore: isOpenProspect && tenant.archived,
    // Reassign Ownership: gated client-side the same way as Archive/Edit — blocked once Active
    // Client, same read-only rule — plus the mock API's own "within your own hierarchy scope" check
    // (see reassignTarget's doc comment).
    canReassign: Boolean(target) && isOpenProspect,
    // Logging activities is a prospect-courting action like Edit/Archive — once a bank is an Active
    // Client, Sales has read-only access and any further activity belongs in the Banking module, not
    // here (see updateTenant's own doc comment for the same rule applied to profile edits).
    canAddActivity: isOpenProspect,
    ...resolveTargetActions(tenant, target, access),
  }
}

/**
 * @description The actions that work on a tenant's target, all of which need it still open.
 * @param {Tenant} tenant - The tenant the target belongs to.
 * @param {Pick<Target, 'status'> | undefined} target - The tenant's relevant target, if any.
 * @param {TenantAccess} access - The viewer's access.
 * @returns {Pick<TenantActionPermissions, 'canEditTarget' | 'canUpdateStage' |
 * 'canScheduleFollowUp' | 'canConfirmPayment'>} Which target actions the viewer may take.
 */
function resolveTargetActions(
  tenant: Tenant,
  target: Pick<Target, 'status'> | undefined,
  access: TenantAccess,
): Pick<
  TenantActionPermissions,
  'canEditTarget' | 'canUpdateStage' | 'canScheduleFollowUp' | 'canConfirmPayment'
> {
  const isTargetOpen = target?.status === 'Outstanding'

  return {
    // Edit Target (name/period/priority): only the owner's authorised seniors — a reporting
    // Manager/Head whose downline includes the owner, or Super Admin — never the owner themselves,
    // and only while the target is open on a prospect bank (see updateTargetDetails).
    canEditTarget:
      !access.isOwner && access.isInScope && tenant.status === 'Prospect' && isTargetOpen,
    canUpdateStage: access.isOwner && isTargetOpen,
    // Schedule Follow-up: same audience as Add Activity — the owner, or an authorised reporting
    // Manager/Head in scope — while the target is still open (a Completed/Active-Client target has
    // nothing left to follow up on).
    canScheduleFollowUp: (access.isOwner || access.isInScope) && isTargetOpen,
    canConfirmPayment: access.canConfirmPayment && isTargetOpen,
  }
}

/** An action a tenant's row menu can open on its own. */
export type TenantAction =
  | 'edit'
  | 'editTarget'
  | 'updateStage'
  | 'scheduleFollowUp'
  | 'addActivity'
  | 'reassign'
  | 'confirmPayment'
  | 'restore'
  | 'archive'

/** The permission that unlocks each action. */
export const TENANT_ACTION_PERMISSION: Record<TenantAction, keyof TenantActionPermissions> = {
  edit: 'canEdit',
  editTarget: 'canEditTarget',
  updateStage: 'canUpdateStage',
  scheduleFollowUp: 'canScheduleFollowUp',
  addActivity: 'canAddActivity',
  reassign: 'canReassign',
  confirmPayment: 'canConfirmPayment',
  restore: 'canRestore',
  archive: 'canArchive',
}
